/// <amd-module name="Wit/SprintsHub/SprintsView/Components/Backlog/BacklogTab" />
import * as React from "react";
import { IObservableValue } from "VSS/Core/Observable";
import { IVssPageContext } from "VSS/Platform/Context";
import { Intersection } from "VSSUI/Intersection";
import { Link } from "VSSUI/Link";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { IFilter } from "VSSUI/Utilities/Filter";
import { AgileFilterBar } from "Wit/Common/BacklogContent/AgileFilterBar";
import { BacklogContentView } from "Wit/Common/BacklogContent/BacklogContentView";
import { HubType } from "Wit/Common/BacklogContentService/BacklogContentTreeTypes";
import { IBacklogWorkItemService } from "Wit/Common/BacklogContentService/IBacklogWorkItemService";
import { ICommonBacklogContentService } from "Wit/Common/BacklogContentService/ICommonBacklogContentService";
import { ISprintsHubContentService } from "Wit/Common/BacklogContentService/ISprintsHubContentService";
import { IDirectoryService } from "Wit/DirectoryService/IDirectoryService";
import { DirectoryDropdownProvider } from "Wit/DirectoryView/DirectoryDropdownProvider";
import { FilterManager } from "Wit/Filter/FilterManager";
import { BoardsMessages, ContainerNames } from "Wit/Messages/Components/BoardsMessages";
import { IBoardsMessagesService } from "Wit/Messages/BoardsMessagesService";
import { ISprintTaskboardErrorData } from "Wit/SprintsHub/Common/Contracts/Interfaces";
import * as Resources from "Wit/SprintsHub/SprintsView/Resources";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";

export interface IBacklogTabProps {
    filter: IFilter;
    filterToggled: IObservableValue<boolean>;
    filterManager: FilterManager;
    pageContext: IVssPageContext;
    taskBoardError: ISprintTaskboardErrorData | undefined;
    onMarkTTI: () => void;
    backlogWorkItemService?: IBacklogWorkItemService;
}

export function BacklogTab(props: IBacklogTabProps): JSX.Element {
    const { filter, filterToggled, filterManager, pageContext, taskBoardError } = props;
    const commonBacklogContentService = pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
    const sprintsHubService = pageContext.getService<ISprintsHubContentService>("ISprintsHubContentService");
    const isNewSprintsViewEnabled = isFeatureFlagEnabled(pageContext, FeatureFlags.NewSprintsBacklogsView, false);

    React.useEffect(() => {
        updateTaskBoardErrorMessage(pageContext, taskBoardError);
    }, [taskBoardError]);

    return (
        <div className={`page-content flex-row flex-start ${isNewSprintsViewEnabled ? 'backlog-tab scroll-hidden' : 'full-height h-scroll-hidden'}`}>
            <Intersection>
                <div className={`flex-grow flex-column backlogs-visible-overflow ${isNewSprintsViewEnabled ? '' : 'full-height'}`}>
                    <BoardsMessages className="margin-top-16 flex-noshrink min-icon-height" containerName={ContainerNames.BacklogTab} />
                    <div className="page-content-bottom">
                        <Observer backlogTree={commonBacklogContentService.getBacklogTree()}>
                            {({ backlogTree }) => {
                                if (backlogTree) {
                                    return (
                                        <AgileFilterBar
                                            className={"page-content-top"}
                                            dataSource={commonBacklogContentService}
                                            filter={filter}
                                            filterToggled={filterToggled}
                                            filterManager={filterManager}
                                            hub={HubType.Sprints}
                                            hubService={sprintsHubService}
                                        />
                                    );
                                } else {
                                    return null;
                                }
                            }}
                        </Observer>
                    </div>
                    <BacklogContentView
                        newWorkItemCalloutAnchor={commonBacklogContentService.getNewItemCalloutAnchor()}
                        pageContext={pageContext}
                        directoryDropdownProvider={
                            new DirectoryDropdownProvider({
                                directoryService: pageContext.getService<IDirectoryService>("ISprintsDirectoryService"),
                                artifactTypeName: Resources.Sprints,
                                iconProps: { iconName: "Sprint", ariaHidden: true }
                            })
                        }
                        filterManager={filterManager}
                        hub={HubType.Sprints}
                        toggleFilter={() => (filterToggled.value = !filterToggled.value)}
                        onMarkTTI={props.onMarkTTI}
                        backlogWorkItemService={props.backlogWorkItemService}
                    />
                </div>
            </Intersection>
        </div>
    );
}

function updateTaskBoardErrorMessage(pageContext: IVssPageContext, taskBoardError: ISprintTaskboardErrorData | undefined) {
    const messagesService = pageContext.getService<IBoardsMessagesService>("IBoardsMessagesService");
    messagesService.dismissMessage();
    if (taskBoardError) {
        const { exceptionMessage, primaryLinkHref, primaryLinkText } = taskBoardError.exceptionInfo;
        const renderMessage = () => {
            return (
                <>
                    <span>{exceptionMessage}</span>
                    <Link
                        className="padding-left-4"
                        href={primaryLinkHref}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {primaryLinkText}
                    </Link>
                </>
            );
        };
        messagesService.showMessage({
            renderMessage: renderMessage,
            severity: MessageCardSeverity.Error,
        });
    }
}
